/** Parent evidence uses the genuine front-door cookie; no cockpit credential is copied. */
import { requestJson, apiUrl } from './http';

export type LearningContext = { project_id: string; runtime_id: string; access_mode: string; configured: boolean };
export type Submission = { submission_id: string; revision: number; page_count: number; confirmed_skill_count: number;
  pages: { page_number: number; content_sha256: string; media_type: string; low_confidence: boolean }[];
  confirmations?: { skill_id: string; statement_sha256: string; confirmed_by: string; confirmed_at: string }[];
  ocr?: { pages: { page_number: number; ocr_text: string; low_confidence: boolean }[] } };
export type Learner = { workspace: { workspace_id: string; authority: 'guardian' | 'child_session' }; submissions: Submission[] };
export type Preview = { preview_id: string; preview_digest: string; submission_count: number; expires_in_seconds: number };

export function getLearningContext(runtimeId: string): Promise<LearningContext> {
  return requestJson('/v1/learning/workspaces/' + encodeURIComponent(runtimeId) + '/context') as Promise<LearningContext>;
}
export function learnerPath(context: LearningContext, learnerId: string, tail = ''): string {
  return '/v1/learning/projects/' + encodeURIComponent(context.project_id) + '/workspaces/' + encodeURIComponent(context.runtime_id)
    + '/learners/' + encodeURIComponent(learnerId) + tail;
}
export function readLearner(context: LearningContext, learnerId: string): Promise<Learner> {
  return requestJson(learnerPath(context, learnerId)) as Promise<Learner>;
}
export function learningPost(context: LearningContext, learnerId: string, tail: string, payload: unknown): Promise<unknown> {
  return requestJson(learnerPath(context, learnerId, tail), { method: 'POST', body: JSON.stringify(payload) });
}
export function readSubmission(context: LearningContext, learnerId: string, submissionId: string): Promise<Submission> {
  return requestJson(learnerPath(context, learnerId, '/submissions/' + encodeURIComponent(submissionId))) as Promise<Submission>;
}
export function originalUrl(context: LearningContext, learnerId: string, submissionId: string, page: number): string {
  return apiUrl(learnerPath(context, learnerId, '/submissions/' + encodeURIComponent(submissionId) + '/pages/' + page + '/original'));
}
export type ExportOriginal = { submission_id: string; page_number: number; content_sha256: string };

async function verifiedSave(response: Response, filename: string, refused: string, expectedSha256 = ''): Promise<{ blob: Blob; sha256: string }> {
  if (!response.ok) throw new Error(refused);
  const blob = await response.blob();
  const sha256 = response.headers.get('x-content-sha256') || '';
  if (!blob.size || !sha256) throw new Error('Evidence download returned no verified bytes.');
  const calculated = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())))
    .map((byte) => byte.toString(16).padStart(2, '0')).join('');
  if (calculated !== sha256 || (expectedSha256 && calculated !== expectedSha256)) throw new Error('Evidence download checksum did not match.');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a'); link.href = url; link.download = filename;
  link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { blob, sha256 };
}

/** The confirmed JSON packet plus the originals it lists; both work after a plan ends. */
export async function downloadEvidence(context: LearningContext, learnerId: string, preview: Preview): Promise<{bytes: number; sha256: string; originals: ExportOriginal[]}> {
  const response = await fetch(apiUrl(learnerPath(context, learnerId, '/export-download')), {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ preview_id: preview.preview_id, preview_digest: preview.preview_digest, confirm: true }),
  });
  const { blob, sha256 } = await verifiedSave(response, 'learning-evidence.json', 'Evidence download was refused or the preview expired. Preview again.');
  let originals: ExportOriginal[] = [];
  try {
    const packet = JSON.parse(await blob.text()) as { submissions?: { submission_id: string; pages?: { page_number: number; content_sha256: string }[] }[] };
    originals = (packet.submissions || []).flatMap((row) => (row.pages || []).map((page) =>
      ({ submission_id: row.submission_id, page_number: page.page_number, content_sha256: page.content_sha256 })));
  } catch { originals = []; }
  return { bytes: blob.size, sha256, originals };
}

/** One original page from the same confirmed export; checked against the packet's checksum. */
export async function downloadExportOriginal(context: LearningContext, learnerId: string, preview: Preview, original: ExportOriginal): Promise<{bytes: number; sha256: string}> {
  const response = await fetch(apiUrl(learnerPath(context, learnerId, '/export-download/original')), {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ preview_id: preview.preview_id, preview_digest: preview.preview_digest, confirm: true,
      submission_id: original.submission_id, page_number: original.page_number }),
  });
  const { blob, sha256 } = await verifiedSave(response, 'original-' + original.submission_id + '-p' + original.page_number + '.bin',
    'Original download was refused or the preview expired. Preview again.', original.content_sha256);
  return { bytes: blob.size, sha256 };
}
